import type { NtBlock, NtCheckin, NtHost, NtProjectBlock, NtProposal } from '../../core/types'

/**
 * Project check-ins: the pure part, tested (tests/checkins.test.ts). Approved
 * independent projects grouped into joint projects, what a project's students
 * are doing in a block, and where.
 */

export interface ProjectGroup {
  /** Its students' emails, sorted and joined: stable across re-runs, and what check-ins hang on. */
  key: string
  proposals: NtProposal[]
  emails: string[]
  names: string
  title: string
}

/**
 * Approved projects, joint ones together. Admin or office links partners
 * (partnerEmails); a link from either side joins them, and links chain, so
 * three students linked in a row are one project.
 */
export function projectGroups(proposals: NtProposal[]): ProjectGroup[] {
  const approved = proposals.filter((p) => p.status === 'approved')
  const byEmail = new Map(approved.map((p) => [p.studentEmail, p]))
  const parent = new Map(approved.map((p) => [p.studentEmail, p.studentEmail]))
  const find = (e: string): string => { const q = parent.get(e)!; return q === e ? e : find(q) }
  for (const p of approved) {
    for (const other of p.partnerEmails ?? []) {
      if (!byEmail.has(other)) continue
      const a = find(p.studentEmail), b = find(other)
      if (a !== b) parent.set(a, b)
    }
  }
  const groups = new Map<string, NtProposal[]>()
  for (const p of approved) { const r = find(p.studentEmail); groups.set(r, [...(groups.get(r) ?? []), p]) }
  return [...groups.values()].map((ps) => {
    const sorted = [...ps].sort((a, b) => a.studentName.localeCompare(b.studentName))
    const titles = [...new Set(sorted.map((p) => p.title.trim()).filter(Boolean))]
    return {
      key: sorted.map((p) => p.studentEmail).sort().join(','),
      proposals: sorted, emails: sorted.map((p) => p.studentEmail),
      names: sorted.map((p) => p.studentName).join(' & '),
      title: titles.join(' / ') || 'Independent project',
    }
  }).sort((a, b) => a.names.localeCompare(b.names))
}

/** What the plan says for a day and block (both parts), in order. */
export function blockPlan(p: Pick<NtProposal, 'plan'>, day: number, block: NtBlock): NtProjectBlock[] {
  return (p.plan ?? []).filter((b) => b.day === day && b.block === block).sort((a, b) => a.part - b.part)
}

/** The hosts a student is with on a day: the ones that named that day, or every host when none did. */
export function hostsOn(p: Pick<NtProposal, 'hosts'>, day: number): NtHost[] {
  const hs = p.hosts ?? []
  const named = hs.filter((h) => h.days.includes(day))
  return named.length ? named : hs
}

/** Which block it is now: before noon is the morning. */
export function blockNow(now: Date = new Date()): NtBlock {
  return now.getHours() < 12 ? 'AM' : 'PM'
}

/** Check-ins for a project, newest first. */
export function checkinsFor(all: NtCheckin[], key: string): NtCheckin[] {
  const ms = (c: NtCheckin) => (c.at as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0
  return all.filter((c) => c.groupKey === key).sort((a, b) => ms(b) - ms(a))
}

/** Search across names, project titles and host places. */
export function matchesSearch(g: ProjectGroup, q: string): boolean {
  if (!q.trim()) return true
  const hay = [g.names, g.title, ...g.proposals.flatMap((p) => (p.hosts ?? []).map((h) => h.org))].join(' ').toLowerCase()
  return q.toLowerCase().split(/\s+/).every((w) => hay.includes(w))
}

export const ENGAGEMENT: Array<{ k: NtCheckin['engagement']; label: string; tone: 'good' | 'plain' | 'serious' | 'critical' }> = [
  { k: 'exemplary', label: 'Exemplary', tone: 'good' },
  { k: 'meets', label: 'Meets', tone: 'good' },
  { k: 'developing', label: 'Developing', tone: 'serious' },
  { k: 'not-yet', label: 'Not yet', tone: 'critical' },
]
export const PROGRESS: Array<{ k: NtCheckin['progress']; label: string }> = [
  { k: 'ahead', label: 'Ahead of plan' }, { k: 'on-track', label: 'On track' }, { k: 'behind', label: 'Behind plan' },
]
